import { AVAILABLE_YEARS, HIGHCHART_COLORS } from "../../../../libs/constant";

import React,{FC, Suspense, useEffect, useState} from "react";
import Highcharts from 'highcharts/es-modules/masters/highcharts.src'
import "highcharts/es-modules/masters/highcharts-more.src"
import "highcharts/es-modules/masters/modules/exporting.src"
import "highcharts/es-modules/masters/modules/offline-exporting.src"
import "highcharts/es-modules/masters/modules/export-data.src"
import HighchartsReact from 'highcharts-react-official'
import { Row, Spinner, Modal, Button, Col, Form, Pagination } from "react-bootstrap";
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, PaginationState, RowData, SortingState, useReactTable} from '@tanstack/react-table';
import * as _ from 'lodash'
import { getNameByISO3, shortenLargeNumber } from "../../../../libs/misc";
import { TradeDataTable } from "../core/_models";

type CommodityDetailModalProps = {
  params:any;
  handleDisplayToggle:()=>void;
}
interface ExtendedHighchartPiePoint{
  key:string,
  x: number,
  y: number,
  percentage: number
  name:string,
  trade_value: number,
}
interface ExtendedHighchartCreditInterface{
  credits:any
}

const columnHelper = createColumnHelper<TradeDataTable>()

const CommodityDetailModal:FC<CommodityDetailModalProps> = ({
  params,
  handleDisplayToggle
}) => {
  const highchartOptions = {
    chart:{
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      type: 'pie',
      marginTop: 0,
      events: {
        load: function (this:ExtendedHighchartCreditInterface) {
          this.credits.element.onclick = function () { window.open('http://comtrade.un.org', '_blank'); }
        }
      }
    },
    tooltip: {
      headerFormat: '',
      pointFormatter: function () {}
    },
    title:{
      text:''
    },
    series: [{
      data: [1,2,3]
    }],
    plotOptions: {
      pie: {
        size: '60%',
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          formatter: function (this:ExtendedHighchartPiePoint) {
            const {key, percentage} = this
            const dataLabel = (percentage < 0.01) ? '<0.01' : percentage.toFixed(2);
            return `${key}: ${dataLabel}%`;
          },
          style: {
            color: (Highcharts.theme && Highcharts.theme.contrastTextColor) || 'black',
            fontWeight: 'normal'
          },
          // connectorColor: 'silver'
        }
      }
    },
    credits:{
      enabled: true,
      text: `Data source: UN Comtrade`,
      href: '#',
      position: {
        align: 'center'
      }
    },
    exporting: {
      enabled: true
    },
    accessibility: {
      enabled: false
    }
  };
  const columns = [
      columnHelper.accessor('hs',{
          header: 'HS Code',
          cell: info=>{
            const flow = info.row.original.flow
            const hs = info.getValue()
            return (
              <span 
                className="badge cursor-pointer text-primary fw-1000 " 
                style={{background:'rgba(5, 66, 148, 0.1)'}}>
                {hs}
              </span>
              )
            }
        }),
        columnHelper.accessor('description',{
          header: 'Description',
          cell: info=>info.getValue()
        }),
        columnHelper.accessor('val',{
          header: "Value",
          cell: info=>shortenLargeNumber(info.getValue())
        })
    ];
  const [worldTradeValue, setWorldTradeValue] = useState(0)
  const [pieChartOptions, setPieChatOptions] = useState<any>(highchartOptions)
  const [cardTitle, setCardTitle] = useState('')
  const [dataTable, setDataTable] = useState<TradeDataTable[]>()
  const [pagination, setPagination] = React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 20,
    })
  const [globalFilter, setGlobalFilter] = useState<any>([])
  const [sorting, setSorting] = React.useState<SortingState>([])
  const tradeTable = useReactTable({
      data:dataTable || [],
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      onGlobalFilterChange: setGlobalFilter,
      onSortingChange: setSorting,
      state: {
        pagination,
        globalFilter,
        sorting
      },
  })
  const generatePieChartTradeDataByCommodity = () =>{
    const tradeData = params.commodityData.rows
    const tradeDescription = params.description
    const tradeflow = params.tradeflow==='imports'?1:2
    if(tradeData.length<1){
      throw Error("Error")
    }    
    const worldValue = tradeData.filter((trade)=>trade.par==='WLD' && trade.flow===tradeflow)
    setWorldTradeValue(worldValue[0].val)
    const tradeDataSorted = _.chain(tradeData)
                            .filter(obj => obj.par !== 'WLD' && obj.flow===tradeflow)
                            .orderBy('val','desc')
                            .value()
    const topData = tradeDataSorted.map(trade=>{
      return{
        ...trade,
        percentage: ((trade.val/worldValue[0].val)* 100).toFixed(4)
      }
    })
    const graphDataset = [
      ...topData.map((trade, idx)=>{
        return {
          name: getNameByISO3(trade.par),
          y: +trade.percentage,
          trade_value: trade.val,
          color: HIGHCHART_COLORS[idx]
        }
      })
    ]
    setPieChatOptions({
      ...highchartOptions,
      // title:{
      //   text:`Commodity: ${tradeData.hs_code} - ${tradeDescription}`,
      //   align:'left'
      // },
      series: [{
        data: graphDataset,
      }],
      tooltip:{
        headerFormat: '',
        pointFormatter: function (this:ExtendedHighchartPiePoint) {
          const {name, percentage, trade_value, key} = this
          if (name === 'Others') {
            return `
            <strong>${key}</strong>
            <br />Share of Imports: <strong>${percentage.toFixed(2)}%</strong>`;
          } else {
            var formattedPercentage = (percentage < 0.01) ? '<0.01' : percentage.toFixed(2);
            return `
              <strong>${name}</strong>
              <br />Share of Imports: <strong>${formattedPercentage}%</strong>
              <br />Value of Imports: US$ ${shortenLargeNumber(trade_value)}`;
          }
        }
      }
    })
    setCardTitle(`Top ${params.tradeflow} partners of commodity ${worldValue[0].hs} in ${worldValue[0].year}`)
  }

  const generateTableTradeDataByPartner = () =>{
    setDataTable(params.commodityData.rows.map((trade)=>{
      return {
        hs: trade.hs,
        description: trade.description,
        val: trade.total_value
      }
    }))
    setCardTitle(`List of commodities ${params.tradeflow==='exports'?`exported to`:`imported from`} ${getNameByISO3(params.partner)} in ${params.year}`)
  }

  useEffect(()=>{
    setDataTable([])
    if(params.commodityData && params.commodityData.rows?.length>0){
      if(params.type==='chart'){
        generatePieChartTradeDataByCommodity()
      }else if(params.type==='table'){
        generateTableTradeDataByPartner()
      }
    }
  },[params])

  const pagesTable = paginationRange(tradeTable);

  function paginationRange(dataTable) {
    const currentPage = pagination.pageIndex + 1;
    const totalPages = dataTable.getPageCount();
    const maxPagesToShow = 1;
    const halfRange = Math.floor(maxPagesToShow / 2);

    // Ensure startPage and endPage are within valid range
    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    // Adjust range to maintain maxPagesToShow
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      }
    }

    const range:any = [];

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      range.push(1);
      if (startPage > 2) {
        range.push('...');
      }
    }

    // Add pages in the calculated range
    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    // Add last page and ellipsis if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...');
      }
      range.push(totalPages);
    }

    return range;
  }

  const handlePageChange = (page, dataTable) => {
    const currentPage = pagination.pageIndex+1
    const totalPages = dataTable.getPageCount()
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      dataTable.setPageIndex(page-1)
    }
  };

  const handleDisplayPageChange = (e) =>{
    setPagination({
      ...pagination,
      pageIndex: 0,
      pageSize: +e.target.value
    })
  }

  const modalBodyContent = () => {
    switch (params.type) {
      case 'chart':
        return <HighchartsReact highcharts={Highcharts} options={pieChartOptions} />;
      case 'table':
        return (
          <div className="fade-in">
            <div className='table-responsive table-fixhead'>
              <table className='table table-row-bordered table-hover align-middle gs-0'>
                <thead>
                  {tradeTable.getHeaderGroups().map(headerGroup => (
                    <tr className='fw-bold text-gray-800 py-4' key={headerGroup.id}>
                      <th className='ps-4 min-w-75px bg-light rounded-start' >HS Code</th>
                      <th className='min-w-125px bg-light'>Description</th>
                      <th className='min-w-90px text-end rounded-end px-3 bg-light'>Value</th>
                    </tr>
                  ))}
                </thead>
                <tbody className="gy-1 overflow-auto">
                  {tradeTable.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        <td key={row.getVisibleCells()[0].id} className="text-center">
                          {flexRender(
                            row.getVisibleCells()[0].column.columnDef.cell, 
                            row.getVisibleCells()[0].getContext())
                          }
                        </td>
                        <td key={row.getVisibleCells()[1].id} className="text-start">
                          {flexRender(
                            row.getVisibleCells()[1].column.columnDef.cell, 
                            row.getVisibleCells()[1].getContext())
                          }
                        </td>
                        <td key={row.getVisibleCells()[2].id} className="text-end fw-bold px-5">
                          {flexRender(
                            row.getVisibleCells()[2].column.columnDef.cell, 
                            row.getVisibleCells()[2].getContext())
                          }
                        </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Row className='my-5' style={{ fontSize: '0.8em' }}>
              <Col md={4} className='d-flex justify-content-start align-items-center'>
                Display
                <Form.Select
                  size="sm"
                  className="w-80px mx-2"
                  onChange={handleDisplayPageChange}
                  value={pagination.pageSize}>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Form.Select>
                results
              </Col>
              <Col md={8} className='d-flex justify-content-end'>
                <Pagination size="sm">
                  <Pagination.Prev
                    onClick={() => tradeTable.previousPage()}
                    disabled={!tradeTable.getCanPreviousPage()} />
                  {/* Start Dynamic Page */}
                  {pagesTable.map((page, index) => {
                    if (page === '...') {
                      return (
                        <Pagination.Ellipsis key={index} />
                      );
                    }
                    return (
                      <Pagination.Item
                        key={index}
                        active={page === pagination.pageIndex + 1}
                        onClick={() => handlePageChange(page, tradeTable)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  })}
                  {/* End Dynamic Page */}
                  <Pagination.Next
                    onClick={() => tradeTable.nextPage()}
                    disabled={!tradeTable.getCanNextPage()} />
                </Pagination>
              </Col>
            </Row>
          </div>
          
        );
      default:
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height:'300px'}}>
            <Spinner animation="border" variant="primary" className="me-3"/> Loading ...
          </div>
        );
    }
  };

  const commoditySubtitle = () => {
    switch(params.type){
      case 'chart':
        return (
          <Row className="mb-3">
            <small>{`Commodity: ${params.description}`}</small>
          </Row>
        )
      default: return null
    }
  }

  return (
    <Modal 
      show={params.show} onHide={handleDisplayToggle} size="lg" centered>
      <Modal.Header closeButton className="d-flex">
        <Modal.Title>{cardTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{minHeight:'300px'}}>
        {commoditySubtitle()}
        <Row>
          <Suspense fallback={<Spinner animation="border" variant="primary" />}>
            {modalBodyContent()}
          </Suspense>
        </Row>        
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline" onClick={handleDisplayToggle}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export {CommodityDetailModal}